import { memo, useCallback, useState } from 'react';
import { TableListFieldItemValue, Textarea } from '@page/ui';

interface TextareaFieldProps {
  value: string;
  actions?: React.ReactNode;
  alwaysDisplayActions?: boolean;
  className?: string;
  name?: string;
  renderDisplayValue?: (value: string) => React.ReactNode;
  onSave?: (value: string) => void;
  onEdit?: () => void;
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  clickToEdit?: boolean;
}

const TextareaFieldBase = ({
  value,
  actions,
  alwaysDisplayActions = false,
  className,
  name,
  renderDisplayValue,
  onSave,
  onEdit,
  isEditing: externalIsEditing,
  setIsEditing: externalSetIsEditing,
  clickToEdit = true,
}: TextareaFieldProps) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const isControlled = externalIsEditing !== undefined;
  const isEditing = isControlled ? externalIsEditing : internalIsEditing;
  const setIsEditing = isControlled ? externalSetIsEditing : setInternalIsEditing;

  const handleEdit = useCallback(() => {
    setEditValue(value);
    setIsEditing?.(true);
    onEdit?.();
  }, [value, setIsEditing, onEdit]);

  const handleSave = useCallback(() => {
    setIsEditing?.(false);
    onSave?.(editValue);
  }, [editValue, setIsEditing, onSave]);

  const displayValue = renderDisplayValue ? renderDisplayValue(value) : value || ' ';

  return (
    <TableListFieldItemValue
      actions={actions}
      alwaysDisplayActions={alwaysDisplayActions}
      className={className}
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      renderEditMode={() => (
        <Textarea
          className="p-3 font-body-md text-text-primary font-normal"
          name={name}
          value={editValue}
          autoFocus
          onChange={(event) => setEditValue(event.target.value)}
        />
      )}
      clickToEdit={clickToEdit}
    >
      {displayValue}
    </TableListFieldItemValue>
  );
};

export const TextareaField = memo(TextareaFieldBase);
